/**
 * d-collection-sort-availability.js
 *
 * Ordena os produtos da coleção/busca colocando disponíveis antes dos
 * esgotados. Dois modos, escolhidos automaticamente por seção:
 *
 * ── Modo "advanced" (sort global cross-page) ────────────────────────────────
 * Ativo quando: paginação é "number" (links reais, sem AJAX), há mais de 1
 * página, e a seção não tem blocos decorativos (image_card) nem resultados
 * não-produto (artigos/páginas na busca).
 * Busca todas as páginas via fetch, une + ordena os cards e reconstrói uma
 * paginação virtual client-side (sem recarregar a página), assim um produto
 * esgotado na página 1 pode "cair" pra última página de verdade.
 *
 * ── Modo "simple" (reorder do que já está na tela) ──────────────────────────
 * Usado quando o modo advanced não se aplica (paginação infinite/load_more,
 * página única, blocos decorativos, ou resultados mistos na busca). Apenas
 * reordena os itens já carregados, disponíveis primeiro. Correto dentro do
 * que está na tela, mas não persegue esgotados através de páginas ainda não
 * carregadas.
 *
 * ── Pré-requisitos no HTML (já configurados em) ─────────────────────────────
 * sections/main-collection-product-grid.liquid e sections/main-search.liquid:
 *
 *   1. #ProductsList com data attributes:
 *        data-sort-availability-id="{{ section.id }}"
 *        data-sort-availability-per-page="{{ products_per_page }}"
 *        data-sort-availability-total-pages="{{ paginate.pages }}"
 *        data-sort-availability-pagination-type="{{ section.settings.pagination }}"
 *        data-sort-availability-advanced-ok="true|false"
 *
 *   2. Cada item de produto direto filho de #ProductsList com:
 *        data-product-available="{{ product.available }}"
 *      (itens sem esse atributo — ex: image_card, artigos, páginas — não são
 *      movidos; o sort só reordena entre si os itens marcados.)
 *
 *   3. #ProductGridContainer envolvendo #ProductsList e a paginação nativa
 *      ({% render 'pagination' %}) — observado via MutationObserver pra
 *      detectar re-renders do facets.js (filtros/sort AJAX).
 *
 *   4. <template data-sort-availability-icons hidden> com os ícones de seta
 *      (icon-caret-left/right) — usados na paginação virtual do modo advanced.
 *
 * ── Comportamento ────────────────────────────────────────────────────────────
 * - Compatível com filtros/ordenação AJAX (facets.js dispara re-render de
 *   #ProductGridContainer; um MutationObserver reinicia o sort).
 * - Compatível com load-more/infinite (reordena incrementalmente o que entra).
 * - Compatível com navegação via browser back/forward (popstate) no modo advanced.
 * - Em caso de erro no fetch (modo advanced), cai no reorder simples como fallback.
 */

(function () {
  'use strict';

  // ─── Selectors ──────────────────────────────────────────────────────────
  var GRID_ID = 'ProductsList';
  var GRID_CONTAINER_ID = 'ProductGridContainer';
  var ITEM_ATTR = 'data-product-available';
  var MAX_PAGES = 20;
  var SUPPRESS_MS = 60;

  // ─── State ─────────────────────────────────────────────────────────────
  var state = {
    mode: null, // 'simple' | 'advanced'
    cards: [], // { html, soldOut }[] — only used in advanced mode
    perPage: 24,
    sectionId: '',
    totalPages: 1,
    generation: 0,
    fetching: false,
    prevIconHtml: '<span>&lsaquo;</span>',
    nextIconHtml: '<span>&rsaquo;</span>',
    suppressUntil: 0
  };

  function suppressObserver() {
    state.suppressUntil = Date.now() + SUPPRESS_MS;
  }

  // ─── Read metadata from #ProductsList data attributes ─────────────────
  function readMeta() {
    var el = document.getElementById(GRID_ID);
    if (!el) return null;
    return {
      sectionId: el.dataset.sortAvailabilityId || '',
      perPage: parseInt(el.dataset.sortAvailabilityPerPage, 10) || 24,
      totalPages: parseInt(el.dataset.sortAvailabilityTotalPages, 10) || 1,
      paginationType: el.dataset.sortAvailabilityPaginationType || '',
      advancedOk: el.dataset.sortAvailabilityAdvancedOk === 'true'
    };
  }

  // ─── URL builder — preserves existing filter/sort params ───────────────
  function buildUrl(page, sectionId) {
    var params = new URLSearchParams(window.location.search);
    params.set('page', page);
    params.set('section_id', sectionId);
    return window.location.pathname + '?' + params.toString();
  }

  // ─── Fetch all server pages in parallel ────────────────────────────────
  function fetchAllPages(sectionId, totalPages) {
    var count = Math.min(totalPages, MAX_PAGES);
    var fetches = [];
    for (var i = 1; i <= count; i++) {
      fetches.push(fetch(buildUrl(i, sectionId)).then(function (r) { return r.text(); }));
    }
    return Promise.all(fetches);
  }

  // ─── Extract sortable items from fetched HTML ──────────────────────────
  function extractItems(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var grid = doc.getElementById(GRID_ID);
    if (!grid) return [];
    return Array.from(grid.children)
      .filter(function (el) { return el.hasAttribute(ITEM_ATTR); })
      .map(function (el) {
        return { html: el.outerHTML, soldOut: el.getAttribute(ITEM_ATTR) === 'false' };
      });
  }

  // ─── Sort: available first, sold-out last (stable) ─────────────────────
  function sortItems(items) {
    var available = items.filter(function (c) { return !c.soldOut; });
    var soldOut = items.filter(function (c) { return c.soldOut; });
    return available.concat(soldOut);
  }

  function sortElements(elements) {
    var available = elements.filter(function (el) { return el.getAttribute(ITEM_ATTR) !== 'false'; });
    var soldOut = elements.filter(function (el) { return el.getAttribute(ITEM_ATTR) === 'false'; });
    return available.concat(soldOut);
  }

  // ─── Simple mode: reorder whatever is currently in the DOM ─────────────
  // Items without the availability attribute (image_card blocks, articles,
  // pages) are left untouched in their original slot.
  function reorderCurrent() {
    var grid = document.getElementById(GRID_ID);
    if (!grid) return;

    var children = Array.from(grid.children);
    var slots = [];
    children.forEach(function (el, idx) {
      if (el.hasAttribute(ITEM_ATTR)) slots.push(idx);
    });
    if (slots.length < 2) return;

    var items = slots.map(function (idx) { return children[idx]; });
    var sorted = sortElements(items);

    var result = children.slice();
    var changed = false;
    slots.forEach(function (idx, i) {
      if (result[idx] !== sorted[i]) changed = true;
      result[idx] = sorted[i];
    });
    if (!changed) return;

    suppressObserver();
    result.forEach(function (el) { grid.appendChild(el); });
  }

  // ─── Capture arrow icons from the hidden template ──────────────────────
  function captureIcons(container) {
    var tpl = container.querySelector('template[data-sort-availability-icons]');
    if (!tpl || !tpl.content) return;
    var nodes = tpl.content.children;
    if (nodes[0]) state.prevIconHtml = nodes[0].outerHTML;
    if (nodes[1]) state.nextIconHtml = nodes[1].outerHTML;
  }

  // ─── Update the "Mostrando X–Y de Z produtos" count ────────────────────
  function updateCount(pageNum) {
    var el = document.getElementById('ProductCount');
    if (!el) return;
    var total = state.cards.length;
    var start = (pageNum - 1) * state.perPage + 1;
    var end = Math.min(pageNum * state.perPage, total);
    el.textContent = 'Mostrando ' + start + '–' + end + ' de ' + total + ' produtos';
  }

  // ─── Render a page slice into the grid (advanced mode) ─────────────────
  function renderPage(pageNum) {
    var grid = document.getElementById(GRID_ID);
    if (!grid || state.cards.length === 0) return;

    var start = (pageNum - 1) * state.perPage;
    var slice = state.cards.slice(start, start + state.perPage);
    if (slice.length === 0) return;

    suppressObserver();
    grid.innerHTML = slice.map(function (c) { return c.html; }).join('');
  }

  // ─── Build page-number list with ellipsis truncation ───────────────────
  function buildPageNumbers(current, total) {
    var pages = [];
    var range = [];
    for (var i = Math.max(1, current - 1); i <= Math.min(total, current + 1); i++) range.push(i);

    if (range.indexOf(1) === -1) {
      pages.push({ type: 'page', page: 1 });
      if (range[0] > 2) pages.push({ type: 'ellipsis' });
    }
    range.forEach(function (p) { pages.push({ type: 'page', page: p }); });
    if (range.indexOf(total) === -1) {
      if (range[range.length - 1] < total - 1) pages.push({ type: 'ellipsis' });
      pages.push({ type: 'page', page: total });
    }
    return pages;
  }

  // ─── Build virtual pagination markup (matches snippets/pagination.liquid) ─
  function buildPaginationHtml(currentPage, totalPages) {
    var itemsHtml = '';

    if (currentPage > 1) {
      itemsHtml += '<li><a href="#" class="pagination__item pagination__item--previous pagination__item-arrow motion-reduce" data-vs-page="'
        + (currentPage - 1) + '">' + state.prevIconHtml + '<span>Anterior</span></a></li>';
    }

    buildPageNumbers(currentPage, totalPages).forEach(function (entry) {
      if (entry.type === 'ellipsis') {
        itemsHtml += '<li><span class="pagination__item">&hellip;</span></li>';
        return;
      }
      if (entry.page === currentPage) {
        itemsHtml += '<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current background-2" aria-current="page" aria-label="Página '
          + entry.page + '">' + entry.page + '</a></li>';
      } else {
        itemsHtml += '<li><a href="#" class="pagination__item" data-vs-page="' + entry.page + '" aria-label="Página '
          + entry.page + '">' + entry.page + '</a></li>';
      }
    });

    if (currentPage < totalPages) {
      itemsHtml += '<li><a href="#" class="pagination__item pagination__item--next pagination__item-arrow motion-reduce" data-vs-page="'
        + (currentPage + 1) + '"><span>Próxima</span>' + state.nextIconHtml + '</a></li>';
    }

    return '<nav class="pagination text-center" role="navigation" aria-label="Paginação">'
      + '<ul class="pagination__list list-unstyled flex items-center flex-wrap justify-center" role="list">' + itemsHtml + '</ul>'
      + '</nav>';
  }

  function buildPagination(currentPage) {
    var container = document.getElementById(GRID_CONTAINER_ID);
    if (!container) return;

    var totalPages = Math.ceil(state.cards.length / state.perPage);
    var nav = container.querySelector(':scope > nav.pagination');

    suppressObserver();

    if (totalPages <= 1) {
      if (nav) nav.remove();
      return;
    }

    var html = buildPaginationHtml(currentPage, totalPages);
    if (nav) {
      nav.outerHTML = html;
    } else {
      container.insertAdjacentHTML('beforeend', html);
    }

    container.dataset.sortAvailabilityPage = currentPage;
  }

  // ─── Navigate to a virtual page (advanced mode) ────────────────────────
  function navigateTo(pageNum) {
    renderPage(pageNum);
    buildPagination(pageNum);
    updateCount(pageNum);

    var url = new URL(window.location.href);
    if (pageNum === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', pageNum);
    }
    history.pushState({ sortAvailabilityPage: pageNum }, '', url.toString());

    var container = document.getElementById(GRID_CONTAINER_ID);
    if (container) {
      var top = container.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  function onPaginationClick(e) {
    var link = e.target.closest('a[data-vs-page]');
    if (!link) return;
    e.preventDefault();
    var page = parseInt(link.dataset.vsPage, 10);
    if (!isNaN(page)) navigateTo(page);
  }

  // ─── Advanced mode: fetch every page, sort, virtualize pagination ──────
  function runAdvancedSort() {
    var container = document.getElementById(GRID_CONTAINER_ID);
    var grid = document.getElementById(GRID_ID);
    if (!container || !grid) return;

    captureIcons(container);

    var gen = ++state.generation;
    state.fetching = true;
    grid.style.visibility = 'hidden';

    fetchAllPages(state.sectionId, state.totalPages)
      .then(function (pages) {
        if (gen !== state.generation) return; // stale — a newer sort was triggered

        var all = [];
        pages.forEach(function (html) { all = all.concat(extractItems(html)); });

        state.cards = sortItems(all);
        state.fetching = false;

        renderPage(1);
        buildPagination(1);
        updateCount(1);
        container.removeEventListener('click', onPaginationClick);
        container.addEventListener('click', onPaginationClick);

        grid.style.visibility = '';
      })
      .catch(function (err) {
        console.warn('[sort-availability] Fetch failed, falling back to simple reorder.', err);
        if (gen !== state.generation) return;
        state.fetching = false;
        grid.style.visibility = '';
        state.mode = 'simple';
        reorderCurrent();
      });
  }

  // ─── Orchestrator: decide mode and run it ──────────────────────────────
  function init() {
    var meta = readMeta();
    if (!meta) return;

    state.sectionId = meta.sectionId;
    state.perPage = meta.perPage;
    state.totalPages = meta.totalPages;
    state.cards = [];

    var advanced = meta.advancedOk && meta.paginationType === 'number' && meta.totalPages > 1;

    if (!advanced) {
      state.mode = 'simple';
      reorderCurrent();
      return;
    }

    state.mode = 'advanced';
    runAdvancedSort();
  }

  // ─── MutationObserver — detect facets/sort AJAX re-renders & load-more ─
  var _debounceTimer = null;

  function watchForUpdates() {
    var container = document.getElementById(GRID_CONTAINER_ID);
    if (!container) return;

    var observer = new MutationObserver(function (mutations) {
      if (Date.now() < state.suppressUntil) return;

      var relevant = mutations.some(function (m) {
        if (m.target && m.target.id === GRID_ID) return true;
        return Array.from(m.addedNodes).some(function (n) {
          if (n.nodeType !== 1) return false;
          return n.id === GRID_ID || (typeof n.querySelector === 'function' && !!n.querySelector('#' + GRID_ID));
        });
      });
      if (!relevant) return;

      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(function () {
        if (Date.now() < state.suppressUntil) return;
        state.generation++; // invalidate any in-flight advanced fetch
        init();
      }, 150);
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  // ─── Handle browser back/forward navigation (advanced mode) ───────────
  window.addEventListener('popstate', function (e) {
    if (state.mode === 'advanced' && state.cards.length > 0 && e.state && e.state.sortAvailabilityPage) {
      renderPage(e.state.sortAvailabilityPage);
      buildPagination(e.state.sortAvailabilityPage);
      updateCount(e.state.sortAvailabilityPage);
    }
  });

  // ─── Boot ────────────────────────────────────────────────────────────
  function boot() {
    if (!document.getElementById(GRID_ID)) return;
    init();
    watchForUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
